import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Building2,
  Eye,
  Heart,
  CheckCircle2,
  TrendingUp,
  Calendar,
  Users,
  MessageSquare,
  Star,
  Download,
  Filter,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function Analytics() {
  const { user, isOwner } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalProperties: 0,
    activeListings: 0,
    totalViews: 0,
    totalFavorites: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    completedBookings: 0,
    averageRating: 0,
    viewsData: [],
    topProperties: [],
    ratingDistribution: [],
  });

  // Redirect if not owner
  useEffect(() => {
    if (!isOwner) {
      navigate('/dashboard');
    }
  }, [isOwner, navigate]);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        // Fetch properties
        const propResponse = await fetch(`${API_URL}/api/owner/properties`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        let propsData = [];
        if (propResponse.ok) {
          const data = await propResponse.json();
          propsData = data.properties || [];
          setProperties(propsData);
        }

        // Fetch bookings
        const bookingResponse = await fetch(`${API_URL}/api/bookings/owner`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        let bookingsData = [];
        if (bookingResponse.ok) {
          const data = await bookingResponse.json();
          bookingsData = data.bookings || [];
          setBookings(bookingsData);
        }

        // Fetch reviews
        const reviewResponse = await fetch(`${API_URL}/api/reviews/user/me`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        let reviewsData = [];
        if (reviewResponse.ok) {
          const data = await reviewResponse.json();
          reviewsData = data.reviews || [];
          setReviews(reviewsData);
        }

        // Process analytics
        const activeListings = propsData.filter(p => p.status === 'available').length;
        const totalViews = propsData.reduce((sum, p) => sum + (p.view_count || 0), 0);
        const totalFavorites = propsData.reduce((sum, p) => sum + (p.favorites_count || 0), 0);

        const bookingStats = {
          pending: bookingsData.filter(b => b.status === 'pending').length,
          confirmed: bookingsData.filter(b => b.status === 'confirmed').length,
          completed: bookingsData.filter(b => b.status === 'completed').length,
        };

        const averageRating = reviewsData.length > 0
          ? (reviewsData.reduce((sum, r) => sum + (r.rating || 0), 0) / reviewsData.length).toFixed(1)
          : 0;

        const ratingDistribution = [
          { name: '5★', value: reviewsData.filter(r => r.rating === 5).length },
          { name: '4★', value: reviewsData.filter(r => r.rating === 4).length },
          { name: '3★', value: reviewsData.filter(r => r.rating === 3).length },
          { name: '2★', value: reviewsData.filter(r => r.rating === 2).length },
          { name: '1★', value: reviewsData.filter(r => r.rating === 1).length },
        ];

        // Views over time (mock data based on view counts)
        const viewsData = propsData.map((p, idx) => ({
          week: `Week ${idx + 1}`,
          views: Math.floor(Math.random() * (p.view_count || 0)) + 10,
        })).slice(0, 8);

        // Top performing properties
        const topProperties = propsData
          .sort((a, b) => (b.view_count || 0) - (a.view_count || 0))
          .slice(0, 5)
          .map(p => ({
            id: p.property_id,
            title: p.title,
            views: p.view_count || 0,
            favorites: p.favorites_count || 0,
            status: p.status,
          }));

        setAnalytics({
          totalProperties: propsData.length,
          activeListings,
          totalViews,
          totalFavorites,
          pendingBookings: bookingStats.pending,
          confirmedBookings: bookingStats.confirmed,
          completedBookings: bookingStats.completed,
          averageRating: parseFloat(averageRating),
          viewsData: viewsData.length > 0 ? viewsData : [{ week: 'Week 1', views: 0 }],
          topProperties,
          ratingDistribution,
        });
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
        toast.error('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    if (isOwner) {
      fetchAnalytics();
    }
  }, [isOwner]);

  const statCards = [
    {
      label: 'Total Properties',
      value: analytics.totalProperties,
      icon: Building2,
      color: 'text-blue-500',
      bg: 'bg-blue-50',
    },
    {
      label: 'Active Listings',
      value: analytics.activeListings,
      icon: CheckCircle2,
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
    },
    {
      label: 'Total Views',
      value: analytics.totalViews,
      icon: Eye,
      color: 'text-purple-500',
      bg: 'bg-purple-50',
    },
    {
      label: 'Total Favorites',
      value: analytics.totalFavorites,
      icon: Heart,
      color: 'text-red-500',
      bg: 'bg-red-50',
    },
    {
      label: 'Pending Bookings',
      value: analytics.pendingBookings,
      icon: Calendar,
      color: 'text-amber-500',
      bg: 'bg-amber-50',
    },
    {
      label: 'Confirmed Bookings',
      value: analytics.confirmedBookings,
      icon: CheckCircle2,
      color: 'text-cyan-500',
      bg: 'bg-cyan-50',
    },
    {
      label: 'Completed Bookings',
      value: analytics.completedBookings,
      icon: TrendingUp,
      color: 'text-green-500',
      bg: 'bg-green-50',
    },
    {
      label: 'Avg. Rating',
      value: analytics.averageRating,
      icon: Star,
      color: 'text-yellow-500',
      bg: 'bg-yellow-50',
    },
  ];

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#6366f1'];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="rh-container">
            <div className="flex items-center justify-center h-96">
              <Loader2 className="w-8 h-8 text-accent animate-spin" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="rh-container">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">
                Analytics Dashboard
              </h1>
              <p className="text-slate-500">
                Track your property performance and bookings
              </p>
            </div>
            <Button className="bg-accent hover:bg-accent/90 rounded-full hidden md:flex">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid gap-4 mb-8 md:grid-cols-2 lg:grid-cols-4">
            {statCards.map((stat) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="border-0 shadow-soft h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                        <p className="text-3xl font-bold text-slate-900">
                          {typeof stat.value === 'number' && stat.value % 1 !== 0
                            ? stat.value.toFixed(1)
                            : stat.value}
                        </p>
                      </div>
                      <div className={`w-12 h-12 ${stat.bg} rounded-2xl flex items-center justify-center`}>
                        <stat.icon className={`w-6 h-6 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-white p-1 rounded-full shadow-sm">
              <TabsTrigger value="overview" className="rounded-full">
                Overview
              </TabsTrigger>
              <TabsTrigger value="properties" className="rounded-full">
                Properties
              </TabsTrigger>
              <TabsTrigger value="bookings" className="rounded-full">
                Bookings
              </TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-full">
                Reviews
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Views Over Time */}
              {analytics.viewsData.length > 0 && (
                <Card className="border-0 shadow-soft">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-purple-500" />
                      Views Over Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="w-full h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.viewsData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="week" stroke="#64748b" />
                          <YAxis stroke="#64748b" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1e293b',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#fff',
                            }}
                          />
                          <Bar dataKey="views" fill="#a78bfa" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Rating Distribution */}
              {analytics.ratingDistribution.some(r => r.value > 0) && (
                <Card className="border-0 shadow-soft">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-500" />
                      Rating Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col md:flex-row items-center justify-center gap-8">
                    <div className="w-full md:w-80 h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={analytics.ratingDistribution}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${value}`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {COLORS.map((color, index) => (
                              <Cell key={`cell-${index}`} fill={color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      {analytics.ratingDistribution.map((item, idx) => (
                        <div key={item.name} className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: COLORS[idx] }}
                          />
                          <span className="text-sm font-medium text-slate-700">
                            {item.name}: {item.value} reviews
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Properties Tab */}
            <TabsContent value="properties">
              <Card className="border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    Top Performing Properties
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.topProperties.length === 0 ? (
                    <div className="text-center py-8">
                      <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No properties yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Property</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Views</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Favorites</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.topProperties.map((prop) => (
                            <tr key={prop.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                              <td className="py-4 px-4">
                                <button
                                  onClick={() => navigate(`/property/${prop.id}`)}
                                  className="text-accent hover:underline font-medium line-clamp-1"
                                >
                                  {prop.title}
                                </button>
                              </td>
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-2">
                                  <Eye className="w-4 h-4 text-purple-500" />
                                  <span className="text-slate-900 font-medium">{prop.views}</span>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-2">
                                  <Heart className="w-4 h-4 text-red-500" />
                                  <span className="text-slate-900 font-medium">{prop.favorites}</span>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                <Badge
                                  className={
                                    prop.status === 'available'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : prop.status === 'reserved'
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-slate-100 text-slate-700'
                                  }
                                >
                                  {prop.status.charAt(0).toUpperCase() + prop.status.slice(1)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Bookings Tab */}
            <TabsContent value="bookings">
              <Card className="border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-amber-500" />
                    Booking Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {bookings.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No bookings yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Property</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Guest</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Check-in</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bookings.map((booking) => (
                            <tr key={booking.booking_id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                              <td className="py-4 px-4 text-slate-900 font-medium line-clamp-1">
                                {booking.property?.title || 'N/A'}
                              </td>
                              <td className="py-4 px-4 text-slate-700">
                                {booking.guest?.name || 'N/A'}
                              </td>
                              <td className="py-4 px-4 text-slate-700">
                                {booking.check_in_date
                                  ? new Date(booking.check_in_date).toLocaleDateString()
                                  : 'N/A'}
                              </td>
                              <td className="py-4 px-4">
                                <Badge
                                  className={
                                    booking.status === 'confirmed'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : booking.status === 'pending'
                                      ? 'bg-amber-100 text-amber-700'
                                      : booking.status === 'completed'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-slate-100 text-slate-700'
                                  }
                                >
                                  {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || 'N/A'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews">
              <Card className="border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-500" />
                    Recent Reviews
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reviews.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No reviews yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {reviews.slice(0, 10).map((review) => (
                        <motion.div
                          key={review.review_id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="border border-slate-200 rounded-lg p-4 hover:border-accent transition"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-semibold text-slate-900">
                                {review.reviewer?.name || 'Anonymous'}
                              </p>
                              <p className="text-sm text-slate-500">
                                {review.property?.title || 'Property'}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < (review.rating || 0)
                                      ? 'fill-yellow-400 text-yellow-400'
                                      : 'text-slate-300'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          {review.comment && (
                            <p className="text-slate-700 text-sm mb-2">{review.comment}</p>
                          )}
                          <p className="text-xs text-slate-400">
                            {review.created_at
                              ? new Date(review.created_at).toLocaleDateString()
                              : ''}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
